import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActionsDropdown from './ActionsDropdown';
import PaginationControls from './PaginationControls';
import { securityService } from './serviceType';

const SubscriptionsTable = ({
  subscriptions,
  totalItems,
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  onPageChange,
  onExtend,
  onCancel,
  onDelete,
  getStatusBadge,
  formatDate,
  showAllColumns = false,
  onAddSubscription // ADD THIS PROP
}) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const navigate = useNavigate();

  const toggleRow = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const handleViewDetails = (subscriptionId) => {
    navigate(`/subscription/${subscriptionId}`);
  };

  if (subscriptions.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <div className="text-gray-400 text-6xl mb-4">📭</div>
        <p className="text-gray-500 text-lg">No subscriptions found</p>
        <p className="text-gray-400 text-sm mt-2">Subscriptions will appear here once customers start signing up</p>
        <button
          onClick={onAddSubscription}
          className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          + Add First Subscription
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      {/* Header with Add Subscription Button */}
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-800">All Subscriptions</h3>
          <span className="text-sm text-gray-500">({totalItems} total)</span>
        </div>
        <button
          onClick={onAddSubscription}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Subscription
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                {/* Expand/collapse column */}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Subscription ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Plan
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subscriptions.map((subscription, index) => {
              const isExpanded = expandedRow === index;
              const subscriptionId = subscription.subscription_id || subscription.subscriptionId;
              const security = securityService(subscription);

              return (
                <React.Fragment key={subscription.id || index}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => toggleRow(index)}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none transition-transform duration-200"
                        aria-label="Toggle details"
                      >
                        <svg
                          className={`w-5 h-5 transform transition-transform duration-200 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-mono text-xs bg-gray-100 px-2 py-1 rounded inline-block">
                        {subscriptionId}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {subscription.customer_name || subscription.customerName}
                      </div>
                      <div className="text-xs text-gray-400 truncate max-w-[150px]">
                        {subscription.email}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {security && (
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium mr-1">
                          {security.label}
                        </span>
                      )}
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {subscription.selected_plan || subscription.selectedPlan}
                      </span>
                      {(subscription.isCustom || subscription.custom) && (
                        <div className="text-[11px] text-gray-500 mt-1">
                          {subscription.cpuCores || 0} vCPU / {subscription.ramGb || 0} GB RAM
                          {(subscription.extraStorageNvmeGb > 0 || subscription.extraStorageHddGb > 0) && (
                            <span>
                              {' '}· {subscription.extraStorageNvmeGb || 0} NVMe / {subscription.extraStorageHddGb || 0} SSD
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        ₵{subscription.total_price || subscription.totalPrice}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(subscription.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewDetails(subscriptionId)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          aria-label="View details"
                        >
                          View
                        </button>
                        <ActionsDropdown
                          subscription={subscription}
                          onExtend={onExtend}
                          onCancel={onCancel}
                          onDelete={onDelete}
                        />
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Row - Shows additional details */}
                  {isExpanded && (
                    <tr className="bg-gray-50">
                      <td colSpan="7" className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                              Contact Information
                            </h4>
                            <div className="space-y-1">
                              <p className="text-sm">
                                <span className="text-gray-500">Email:</span>{' '}
                                <span className="text-gray-900">{subscription.email}</span>
                              </p>
                              <p className="text-sm">
                                <span className="text-gray-500">Phone:</span>{' '}
                                <span className="text-gray-900">
                                  {subscription.phone_number || subscription.phoneNumber || 'N/A'}
                                </span>
                              </p>
                            </div>
                          </div>
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                              Subscription Details
                            </h4>
                            <div className="space-y-1">
                              <p className="text-sm">
                                <span className="text-gray-500">{security ? security.detailLabel : 'Plan'}:</span>{' '}
                                <span className="text-gray-900">{subscription.selected_plan || subscription.selectedPlan}</span>
                              </p>
                              {!security && (
                                <p className="text-sm">
                                  <span className="text-gray-500">OS:</span>{' '}
                                  <span className="text-gray-900">{subscription.selected_os || subscription.selectedOS || 'N/A'}</span>
                                </p>
                              )}
                              <p className="text-sm">
                                <span className="text-gray-500">Billing Cycle:</span>{' '}
                                <span className="text-gray-900">{subscription.billing_cycle || subscription.billingCycle}</span>
                              </p>
                              <p className="text-sm">
                                <span className="text-gray-500">Quantity:</span>{' '}
                                <span className="text-gray-900">{subscription.quantity || 1}</span>
                              </p>
                              {subscription.dedicatedIPs > 0 && (
                                <p className="text-sm">
                                  <span className="text-gray-500">Dedicated IPs:</span>{' '}
                                  <span className="text-gray-900">{subscription.dedicatedIPs}</span>
                                </p>
                              )}
                              {(subscription.isCustom || subscription.custom) && (
                                <>
                                  <p className="text-sm">
                                    <span className="text-gray-500">CPU:</span>{' '}
                                    <span className="text-gray-900">{subscription.cpuCores || 0} vCPU</span>
                                  </p>
                                  <p className="text-sm">
                                    <span className="text-gray-500">RAM:</span>{' '}
                                    <span className="text-gray-900">{subscription.ramGb || 0} GB</span>
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                              Dates
                            </h4>
                            <div className="space-y-1">
                              <p className="text-sm">
                                <span className="text-gray-500">Start Date:</span>{' '}
                                <span className="text-gray-900">{formatDate(subscription.start_date || subscription.startDate)}</span>
                              </p>
                              <p className="text-sm">
                                <span className="text-gray-500">Expiration:</span>{' '}
                                <span className="text-gray-900">{formatDate(subscription.expiration_date || subscription.expirationDate)}</span>
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Extra Storage if exists */}
                        {(subscription.extraStorageNvmeGb > 0 || subscription.extraStorageHddGb > 0) && (
                          <div className="mt-3">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                              Extra Storage
                            </h4>
                            <div className="flex space-x-4">
                              {subscription.extraStorageNvmeGb > 0 && (
                                <span className="text-sm text-gray-700">
                                  NVMe: <span className="font-medium">{subscription.extraStorageNvmeGb} GB</span>
                                </span>
                              )}
                              {subscription.extraStorageHddGb > 0 && (
                                <span className="text-sm text-gray-700">
                                  SSD: <span className="font-medium">{subscription.extraStorageHddGb} GB</span>
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
                          <button
                            onClick={() => handleViewDetails(subscriptionId)}
                            className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                          >
                            View Full Details
                          </button>
                          <button
                            onClick={() => {
                              onExtend(subscription);
                              setExpandedRow(null);
                            }}
                            className="text-sm bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                          >
                            Extend
                          </button>
                          <button
                            onClick={() => {
                              onCancel(subscription);
                              setExpandedRow(null);
                            }}
                            className="text-sm bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              onDelete(subscription);
                              setExpandedRow(null);
                            }}
                            className="text-sm bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          startIndex={startIndex}
          endIndex={endIndex}
          totalItems={totalItems}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

export default SubscriptionsTable;